import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { id: 'general', name: 'General', icon: '⚙️' },
    { id: 'notifications', name: 'Notifications', icon: '🔔' },
    { id: 'data', name: 'Data & Privacy', icon: '🔒' },
    { id: 'integrations', name: 'Integrations', icon: '🔗' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 mt-1">Manage your account and application preferences</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">General Settings</h3>
                  <p className="text-sm text-gray-500">Manage your application preferences and display options</p>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Time Zone</label>
                    <select className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option value="CST">Central Standard Time (CST)</option>
                      <option value="EST">Eastern Standard Time (EST)</option>
                      <option value="PST">Pacific Standard Time (PST)</option>
                      <option value="MST">Mountain Standard Time (MST)</option>
                      <option value="AKST">Alaska Standard Time (AKST)</option>
                      <option value="HST">Hawaii Standard Time (HST)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">All timestamps will be displayed in your selected time zone</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                    <select className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option value="MM/DD/YYYY">MM/DD/YYYY (12/25/2024)</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY (25/12/2024)</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-25)</option>
                      <option value="MMM DD, YYYY">MMM DD, YYYY (Dec 25, 2024)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number Format</label>
                    <select className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option value="US">US Format (1,234.56)</option>
                      <option value="EU">European Format (1.234,56)</option>
                      <option value="SI">SI Format (1 234.56)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dashboard Refresh Rate</label>
                    <select className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option value="30">30 seconds</option>
                      <option value="60">1 minute</option>
                      <option value="300">5 minutes</option>
                      <option value="900">15 minutes</option>
                      <option value="3600">1 hour</option>
                      <option value="manual">Manual refresh only</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">How often the dashboard data automatically refreshes</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Default Page Size</label>
                    <select className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                      <option value="10">10 items per page</option>
                      <option value="25">25 items per page</option>
                      <option value="50">50 items per page</option>
                      <option value="100">100 items per page</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Number of items displayed in tables and lists by default</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Auto-save Forms</p>
                      <p className="text-sm text-gray-500">Automatically save form data as you type</p>
                    </div>
                    <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm">Enabled</button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Show Welcome Tour</p>
                      <p className="text-sm text-gray-500">Display helpful tips and tutorials for new features</p>
                    </div>
                    <button className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm">Disabled</button>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Account Security</h4>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Change Password</p>
                        <p className="text-sm text-gray-500">Update your account password</p>
                      </div>
                      <Link 
                        to="/change-password"
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Change Password
                      </Link>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                    Reset to Defaults
                  </button>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors">
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Notification Preferences</h3>
                  <p className="text-sm text-gray-500">Choose how you want to be notified</p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                      <p className="text-sm text-gray-500">Receive updates via email</p>
                    </div>
                    <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm">Enabled</button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Weekly Reports</p>
                      <p className="text-sm text-gray-500">Get weekly analytics summaries</p>
                    </div>
                    <button className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm">Disabled</button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">New Member Alerts</p>
                      <p className="text-sm text-gray-500">Notify when new members join</p>
                    </div>
                    <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm">Enabled</button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Journey Stage Changes</p>
                      <p className="text-sm text-gray-500">Notify when members progress to new journey stages</p>
                    </div>
                    <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm">Enabled</button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">System Maintenance</p>
                      <p className="text-sm text-gray-500">Receive notifications about system updates and maintenance</p>
                    </div>
                    <button className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm">Disabled</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'data' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Data & Privacy</h3>
                  <p className="text-sm text-gray-500">Manage your data and privacy settings</p>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900">Data Export</h4>
                    <p className="text-sm text-blue-800 mt-1">Download a copy of your analytics data</p>
                    <button className="mt-2 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700">
                      Export Data
                    </button>
                  </div>
                  
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-yellow-900">Data Retention</h4>
                    <p className="text-sm text-yellow-800 mt-1">Manage how long your data is stored</p>
                    <button className="mt-2 bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700">
                      Configure Retention
                    </button>
                  </div>
                  
                  <div className="bg-red-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-900">Delete Account</h4>
                    <p className="text-sm text-red-800 mt-1">Permanently delete your account and data</p>
                    <button className="mt-2 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700">
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Integrations</h3>
                  <p className="text-sm text-gray-500">Manage connected services and APIs</p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📊</span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Planning Center Online</p>
                        <p className="text-sm text-gray-500">Connected • Last sync: 2 hours ago</p>
                      </div>
                    </div>
                    <button className="text-blue-600 text-sm hover:text-blue-700">Manage</button>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-600 text-sm">📧</span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Email Service</p>
                        <p className="text-sm text-gray-500">Not connected</p>
                      </div>
                    </div>
                    <button className="text-blue-600 text-sm hover:text-blue-700">Connect</button>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-600 text-sm">📈</span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Google Analytics</p>
                        <p className="text-sm text-gray-500">Not connected</p>
                      </div>
                    </div>
                    <button className="text-blue-600 text-sm hover:text-blue-700">Connect</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 